import React, { useEffect, useState } from 'react';
import { ButtonRounded, StatusMessage, TextInput } from 'is24-corecss';
import { IconS24Close24, IconS24ArrowLeft24 } from '@is24/cosma-ui-icons';
import { useTranslation } from 'react-i18next';
import { VerificationCompletionData } from '../../@types/VerificationCompletionData';
import { VerificationStatus } from '../../@types/VerificationStatus';
import { ApiResponseError } from '../../api/ApiResponseError';
import { sendWebIdTan, verifyWebIdTan } from '../../api/webid/webIdVerification';
import { useTvsState } from '../../utils/useTvsState';
import { useClientConfig } from '../../utils/clientConfigContext';
import './WebIdTanPage.css';
import IdentificationInfoContent from '../../components/identificationInfoContent/IdentificationInfoContent';

interface WebIdTanPageProps {
  phoneNumber: string | null;
  sessionId: string;
  clearPhoneNumber: () => void;
  onVerificationComplete?: (status: VerificationStatus, data: VerificationCompletionData) => void;
}

const TAN_RESEND_SECONDS = 30;
const tanResendKey = (phoneNumber: string | null): string => `tvs:webid:tan:resendUntil:${phoneNumber ?? 'unknown'}`;

const readRemainingSeconds = (phoneNumber: string | null): number => {
  if (typeof window === 'undefined') {
    return TAN_RESEND_SECONDS;
  }

  const rawExpiresAt = window.sessionStorage.getItem(tanResendKey(phoneNumber));
  if (!rawExpiresAt) {
    return TAN_RESEND_SECONDS;
  }

  const expiresAt = Number(rawExpiresAt);
  if (Number.isNaN(expiresAt)) {
    return TAN_RESEND_SECONDS;
  }

  const remainingSeconds = Math.ceil((expiresAt - Date.now()) / 1000);
  return Math.max(0, remainingSeconds);
};

const storeResendExpiry = (phoneNumber: string | null): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const expiresAt = Date.now() + TAN_RESEND_SECONDS * 1000;
  window.sessionStorage.setItem(tanResendKey(phoneNumber), expiresAt.toString());
};

const clearResendExpiry = (phoneNumber: string | null): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(tanResendKey(phoneNumber));
};

const WebIdTanPage: React.FC<WebIdTanPageProps> = ({ phoneNumber, sessionId, clearPhoneNumber }) => {
  const { t } = useTranslation();
  const { userId } = useClientConfig();
  const [, setTvsState] = useTvsState();
  const [tan, setTan] = useState('');
  const [tanError, setTanError] = useState('');
  const [requestError, setRequestError] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [isResendPending, setIsResendPending] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(() => readRemainingSeconds(phoneNumber));
  const isResendAvailable = countdownSeconds === 0;

  useEffect(() => {
    if (countdownSeconds === 0) {
      clearResendExpiry(phoneNumber);
      return;
    }

    const timerId = window.setInterval(() => {
      setCountdownSeconds((previousSeconds) => {
        if (previousSeconds <= 1) {
          window.clearInterval(timerId);
          clearResendExpiry(phoneNumber);
          return 0;
        }

        return previousSeconds - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [countdownSeconds]);

  const clearSession = () => {
    clearPhoneNumber();
    clearResendExpiry(phoneNumber);
  };

  const onClose = () => {
    clearSession();
    setTvsState('');
  };

  const onVerifyTan = async () => {
    if (!tan.trim()) {
      setTanError(t('VerificationLandingPage.tanError' as any));
      return;
    }
    setTanError('');
    setRequestError('');
    setIsPending(true);

    try {
      if (!sessionId) {
        setRequestError(t('errors.webid.bad.request' as any));
        return;
      }
      await verifyWebIdTan(tan.trim(), sessionId, userId);
      clearSession();
      setTvsState('webid_verification_check_page');
    } catch (error) {
      if (error instanceof ApiResponseError && error.responseStatus === 400) {
        setRequestError(t('errors.webid.invalidTan' as any));
      } else {
        setRequestError(t('errors.default'));
      }
    } finally {
      setIsPending(false);
    }
  };

  const onResendTan = () => {
    if (!isResendAvailable || isResendPending) {
      return;
    }

    setIsResendPending(true);
    setRequestError('');
    void (async () => {
      try {
        if (!sessionId) {
          setRequestError(t('errors.webid.bad.request' as any));
          return;
        }
        await sendWebIdTan(sessionId, userId);
        setCountdownSeconds(TAN_RESEND_SECONDS);
        storeResendExpiry(phoneNumber);
      } catch (error) {
        setRequestError(t('errors.default'));
      } finally {
        setIsResendPending(false);
      }
    })();
  };

  const goBackToLandingPage = () => {
    setTvsState('verification_landing_page');
  };

  return (
    <div aria-modal='true' className='modal-backdrop' style={{ zIndex: 2000 }}>
      <div className='modal-container' style={{ width: 601 }}>
        <div className='modal-title-wrapper'>
          <h4 className='modal-headline grid-flex'>
            <IconS24ArrowLeft24
              onClick={goBackToLandingPage}
              className='align-center grid-align-center margin-right-xxl'
            />
            {t('VerificationLandingPage.tanHeading' as any)}
          </h4>
          <IconS24Close24 onClick={onClose} data-testid='modal-close-icon' className='tvs-close-button' />
        </div>
        <div className='modal-content-wrapper display-flex margin-top-m'>
          <div className='modal-content palm-display-flex'>
            {requestError && (
              <StatusMessage
                data-testid='error-message'
                className='margin-bottom-xxl'
                headline={t('VerificationLandingPage.errorHeading')}
                message={requestError}
                statusType='error'
              />
            )}
            <IdentificationInfoContent
              informationText={'VerificationLandingPage.tanDescription'}
              translationValues={{ phoneNumber: phoneNumber || '' }}
            />
            <div className='desk-one-half margin-top-m'>
              <TextInput
                placeholder='123456'
                data-testid='tan-input'
                id='tan'
                label={t('VerificationLandingPage.inputLabelTan' as any)}
                mandatory={true}
                onChange={(event) => setTan(event.target.value.replace(/\s/g, ''))}
                valid={tanError.length === 0}
                captionText={tanError}
                value={tan}
                maxLength={6}
              />
            </div>
            <div className='grid grid-flex grid-align-center grid-justify-start margin-top-l margin-bottom-xxl'>
              <div className='grid-item align-right margin-right-s'>
                {t('VerificationLandingPage.tanNotReceived' as any)}
              </div>
              <button
                type='button'
                className={`link-tag ${isResendAvailable ? 'active-otp-send' : 'inactive-otp-send'} margin-bottom-none`}
                data-testid='resend-link'
                onClick={onResendTan}
              >
                {t('VerificationLandingPage.tanResendLink' as any)}{' '}
                {!isResendAvailable ? (
                  <span className='countdown-container'>
                    {t('VerificationLandingPage.tanCountdownPrefix' as any)}
                    <span className='countdown-time' data-testid='countdown-text'>
                      {countdownSeconds}
                    </span>
                    {t('VerificationLandingPage.tanCountdownSuffix' as any)}
                  </span>
                ) : null}
              </button>
            </div>
          </div>
        </div>
        <div className='modal-footer verification-modal-footer'>
          <div className='grid grid-flex'>
            <div className='grid-item one-whole palm-one-whole'>
              <div className='grid-flex grid-justify-end verification-footer-div palm-one-whole'>
                <ButtonRounded
                  data-testid='verify-tan-button'
                  className='verification-footer-button'
                  loading={isPending}
                  disabled={tan.trim().length === 0}
                  label={t('VerificationLandingPage.verifyTanButtonText' as any)}
                  size='medium'
                  onClick={onVerifyTan}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebIdTanPage;
